import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import type { Brand, BrandQuery, BrandQueryAxis } from '@traaker/shared';
import { BRAND_QUERY_TEXT_MAX_CHARS } from '@traaker/shared';
import { brandQueriesApi, type GeneratedQueryProposal } from '../../services/brand-queries';

const AXIS_LABELS: Record<BrandQueryAxis, string> = {
  narrative: 'Narrative',
  authority: 'Autorité',
  referral: 'Referral',
};

const LEVEL_LABELS: Record<string, string> = {
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
  L5: 'L5',
  pending: '…',
};

interface QueryManagerProps {
  brand: Brand;
  axis: BrandQueryAxis;
  onChanged?: () => void;
}

export default function QueryManager({ brand, axis, onChanged }: QueryManagerProps) {
  const [queries, setQueries] = useState<BrandQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<BrandQuery | null>(null);
  const [creating, setCreating] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const zones = Array.from(
    new Set(
      (brand.locations || []).map((l) => l.zone).filter(Boolean) as string[],
    ),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brandQueriesApi.list(brand.brandId, axis);
      setQueries(data.filter((q) => q.status === 'active'));
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [brand.brandId, axis]);

  useEffect(() => {
    load();
  }, [load]);

  const handleArchive = async (q: BrandQuery) => {
    try {
      await brandQueriesApi.archive(brand.brandId, axis, q.queryId);
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || 'Erreur de suppression');
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            Requêtes {AXIS_LABELS[axis]} ({queries.length})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setRegenOpen(true)}
          >
            Générer depuis GEO Strategy
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            Ajouter
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : queries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Aucune requête. Ajoutez-en une ou générez-les depuis votre GEO Strategy.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Requête</TableCell>
                  {axis === 'referral' && <TableCell width={80}>Niveau</TableCell>}
                  {axis === 'referral' && <TableCell width={140}>Zone</TableCell>}
                  <TableCell width={120}>Source</TableCell>
                  <TableCell width={100} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {queries.map((q) => (
                  <TableRow key={q.queryId} hover>
                    <TableCell>
                      <Typography variant="body2">{q.text}</Typography>
                    </TableCell>
                    {axis === 'referral' && (
                      <TableCell>
                        {q.level && q.level !== null ? (
                          <Chip label={LEVEL_LABELS[q.level] || q.level} size="small" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    {axis === 'referral' && (
                      <TableCell>
                        <Typography variant="caption">{q.zone || '—'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={
                          q.source === 'user_added'
                            ? 'Manuel'
                            : q.source === 'user_edited'
                              ? 'Édité'
                              : 'GEO'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Éditer">
                        <IconButton size="small" onClick={() => setEditing(q)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Archiver">
                        <IconButton size="small" onClick={() => handleArchive(q)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <QueryFormDialog
        open={creating}
        axis={axis}
        zones={zones}
        onClose={() => setCreating(false)}
        onSubmit={async (values) => {
          await brandQueriesApi.create(brand.brandId, { axis, ...values });
          setCreating(false);
          await load();
          onChanged?.();
        }}
      />

      <QueryFormDialog
        open={!!editing}
        axis={axis}
        zones={zones}
        initial={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSubmit={async (values) => {
          if (!editing) return;
          await brandQueriesApi.update(brand.brandId, editing.queryId, {
            axis,
            text: values.text,
            zone: values.zone,
          });
          setEditing(null);
          await load();
          onChanged?.();
        }}
      />

      <RegenerateDialog
        open={regenOpen}
        brandId={brand.brandId}
        axis={axis}
        existingHashes={new Set(queries.map((q) => q.textHash))}
        onClose={() => setRegenOpen(false)}
        onApplied={async () => {
          setRegenOpen(false);
          await load();
          onChanged?.();
        }}
      />
    </Card>
  );
}

interface QueryFormValues {
  text: string;
  level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
  zone?: string;
}

function QueryFormDialog({
  open,
  axis,
  zones,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  axis: BrandQueryAxis;
  zones: string[];
  initial?: BrandQuery;
  onClose: () => void;
  onSubmit: (values: QueryFormValues) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [zone, setZone] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setText(initial?.text || '');
      setZone(initial?.zone || '');
      setError('');
    }
  }, [open, initial]);

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError('Le texte est requis.');
      return;
    }
    if (text.length > BRAND_QUERY_TEXT_MAX_CHARS) {
      setError(`Maximum ${BRAND_QUERY_TEXT_MAX_CHARS} caractères.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        text: text.trim(),
        zone: axis === 'referral' ? zone || undefined : undefined,
      });
    } catch (err: any) {
      setError(err?.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Éditer la requête' : 'Nouvelle requête'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Texte de la requête"
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText={`${text.length} / ${BRAND_QUERY_TEXT_MAX_CHARS}`}
            inputProps={{ maxLength: BRAND_QUERY_TEXT_MAX_CHARS }}
          />
          {axis === 'referral' && zones.length > 0 && (
            <Select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
            >
              <MenuItem value="">Sans zone</MenuItem>
              {zones.map((z) => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </Select>
          )}
          {axis === 'referral' && (
            <Typography variant="caption" color="text.secondary">
              Le niveau L1-L5 sera assigné automatiquement après création.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Annuler
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RegenerateDialog({
  open,
  brandId,
  axis,
  existingHashes,
  onClose,
  onApplied,
}: {
  open: boolean;
  brandId: string;
  axis: BrandQueryAxis;
  existingHashes: Set<string>;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState<GeneratedQueryProposal[]>([]);
  const [geoStrategyHash, setGeoStrategyHash] = useState<string>('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyErrors, setApplyErrors] = useState<Array<{ text: string; error: string }>>([]);

  useEffect(() => {
    if (!open) return;
    setApplyErrors([]);
    setSelected({});
    setError('');
    setLoading(true);
    brandQueriesApi
      .regenerate(brandId, axis)
      .then((res) => {
        const fresh = res.proposals.filter((p) => !existingHashes.has(p.textHash));
        setProposals(fresh);
        setGeoStrategyHash(res.geoStrategyHash);
        const initSel: Record<string, boolean> = {};
        for (const p of fresh) initSel[p.textHash] = true;
        setSelected(initSel);
      })
      .catch((err) => setError(err?.message || 'Erreur de génération'))
      .finally(() => setLoading(false));
  }, [open, brandId, axis, existingHashes]);

  const handleApply = async () => {
    const items = proposals
      .filter((p) => selected[p.textHash])
      .map((p) => ({ text: p.text, level: p.level, zone: p.zone }));
    if (items.length === 0) return;
    setApplying(true);
    setApplyErrors([]);
    try {
      const res = await brandQueriesApi.applyRegen(brandId, {
        axis,
        geoStrategyHash,
        items,
      });
      if (res.errors.length > 0) {
        setApplyErrors(res.errors);
      }
      if (res.created.length > 0) {
        await onApplied();
      } else if (res.errors.length === 0) {
        onClose();
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur');
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Générer des requêtes depuis GEO Strategy</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : proposals.length === 0 ? (
          <Alert severity="info">
            Aucune nouvelle requête à proposer — votre liste couvre déjà la GEO Strategy.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {proposals.length} proposition(s). Décochez celles que vous ne voulez pas ajouter.
              Les slots existants au-delà de votre quota seront refusés.
            </Typography>

            {applyErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {applyErrors.length} requête(s) refusée(s) :
                </Typography>
                {applyErrors.slice(0, 3).map((e, i) => (
                  <Typography key={i} variant="caption" display="block">
                    • {e.text.slice(0, 80)} — {e.error}
                  </Typography>
                ))}
              </Alert>
            )}

            <TableContainer component={Box} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Requête</TableCell>
                    {axis === 'referral' && <TableCell width={80}>Niveau</TableCell>}
                    {axis === 'referral' && <TableCell width={140}>Zone</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposals.map((p) => (
                    <TableRow key={p.textHash} hover>
                      <TableCell padding="checkbox">
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={!!selected[p.textHash]}
                              onChange={(e) =>
                                setSelected({ ...selected, [p.textHash]: e.target.checked })
                              }
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.text}</Typography>
                      </TableCell>
                      {axis === 'referral' && (
                        <TableCell>
                          {p.level ? <Chip label={p.level} size="small" /> : '—'}
                        </TableCell>
                      )}
                      {axis === 'referral' && (
                        <TableCell>
                          <Typography variant="caption">{p.zone || '—'}</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          Fermer
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applying || selectedCount === 0}
          startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          {applying ? 'Application…' : `Ajouter ${selectedCount} requête(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
